import type { DiagnosticsModalProps } from './index.types';
import React, { useCallback, useState } from 'react';
import { Button } from '~/lib/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/lib/ui/dialog';

export const DiagnosticsModal: React.FC<DiagnosticsModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  placeholder,
  results,
  isRunning,
  runTest,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = useCallback(async () => {
    if (results) {
      try {
        await navigator.clipboard.writeText(results);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
      }
    }
  }, [results]);

  const handleClose = useCallback(() => {
    setCopySuccess(false);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-[800px]!">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className={'text-left pt-2 text-foreground'}>
            {description}
          </DialogDescription>
        </DialogHeader>

        <pre className="bg-muted p-4 rounded-md max-h-[500px] overflow-y-auto">
          {results || placeholder}
        </pre>

        <DialogFooter className="gap-5.5">
          <Button
            size="lg"
            variant="outline"
            onClick={handleClose}
            className="mt-3 sm:mt-0 bg-transparent"
            autoFocus
          >
            Cancel
          </Button>

          <Button
            size="lg"
            onClick={() => void handleCopy()}
            disabled={!results || isRunning}
          >
            {copySuccess ? 'Copied!' : 'Copy Results'}
          </Button>

          <Button
            size="lg"
            onClick={() => {
              setCopySuccess(false);
              void runTest();
            }}
            disabled={isRunning}
          >
            {results ? 'Re-run Test' : 'Run Tests'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
